/// <amd-module name="Wiki/View/View" />
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import {
    HistoryServiceEventNames,
    INavigationHistoryEntry,
    INavigationStateChangedEvent,
    IVssHistoryService,
    StateMergeOptions
} from "VSS/Platform/History";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { WikiPage, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { OverviewContainer } from "Wiki/Overview/Components/OverviewContainer";
import * as Resources from "Wiki/View/Resources";
import { ActionIds, UrlConstants, WikiDataProviderContributionId } from "Wiki/ViewCommon/Constants";
import { UrlParameters, WikiDataProviderData, WikiInfo, WikiPageInfo, WikiPermission, WikiViewOptions } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { normalizeWikiPagePath } from "Wiki/ViewCommon/Utils/PathHelper";
import { getVersionFromBranchName, isCodeEditEnabled, publish, setWikiWindowTitle, updateUrl } from "Wiki/ViewCommon/Utils/WikiHelper";
import * as WikiPermissionsHelper from "Wiki/ViewCommon/Utils/WikiPermissionsHelper";

export interface IViewProps {}

export interface IViewState {
    wikiViewOptions: WikiViewOptions;
    wikiInfo: WikiInfo | undefined;
    wikiPageInfo: WikiPageInfo | undefined;
    wikiPages: WikiPage[];
    showPublishCodeWikiAction: boolean;
    showCreateNewProjectWikiAction: boolean;
    isHandleBrokenLinksEnabled: boolean;
    isEmptyWiki: boolean;
    isStakeHolder: boolean;
    hasDefaultWikiAccess: boolean;
    permissionError: string | undefined;
    isTfvcOnlyProject: boolean;
    isProjectWikiExisting: boolean;
    isLoading: boolean | undefined;
    isUserGivenWiki: boolean;
    isInitialTreeDataPopulated: boolean;
    isIllegalPath: boolean;
    isRepositoryDisabled: boolean;
}

export class View extends VssComponent<IViewProps, IViewState> {
    private _contributionService: IVssContributionService;
    private _historyService: IVssHistoryService;
    private _reattemptDataProvider: boolean;
    private _reattemptRequestParameters: { [key: string]: any } | undefined;

    constructor(props: IViewProps, context: IVssComponentContext) {
        super(props, context);

        this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = this._contributionService.getData<WikiDataProviderData>(WikiDataProviderContributionId);

        const wikiInfo = _getWikiInfo(this.context.pageContext, data);
        const wikiPageInfo = _getWikiPageInfo(data);
               
        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const { _a, anchor, pagePath, pageId, unfollow } = this._historyService.getState();
        let isLoading = false;
        let isIllegalPath = false;

        // Call the data provider in case :
        // 1. We don't get any data in the data provider
        // 2. pagePath is defined but not found on the server and the wiki page was not requested by pageId
        const isPagePathIncorrect = Boolean(data && !data.landingWikiPage && !pageId && pagePath);
        if (!data || isPagePathIncorrect) {
            isLoading = true;
            this._reattemptDataProvider = true;
            this._reattemptRequestParameters = isPagePathIncorrect ? { pagePath: normalizeWikiPagePath(pagePath) } : undefined;

            publish(
                this.context.pageContext,
                TelemetryConstants.WikiDataProviderCallFromClient,
                wikiInfo && wikiInfo.wiki ? wikiInfo.wiki.type : undefined,
                {
                    pageId: wikiPageInfo && wikiPageInfo.id,
                    isWikiPagePathIncorrect: isPagePathIncorrect
                }
            );
        }

        _publishNonConformantPage(this.context.pageContext, wikiPageInfo, wikiInfo);

        const wikiViewOptions: WikiViewOptions = {
            action: _a || ActionIds.View,
            anchor,
            unfollow: unfollow === "true" ? true : undefined
        };

        this.state = {
            wikiViewOptions,
            wikiInfo,
            wikiPageInfo,
            wikiPages: data && data.pages ? data.pages : [],
            showPublishCodeWikiAction: _getPublishCodeWikiActionFlag(data, wikiInfo),
            showCreateNewProjectWikiAction: _getCreateNewProjectWikiActionFlag(data, wikiInfo),
            isHandleBrokenLinksEnabled: _getHandleBrokenLinksFlag(data),
            isEmptyWiki: _checkIfEmptyWiki(this.context.pageContext, data, pagePath),
            isStakeHolder: data ? data.isStakeholder : true, // default true as it is less priviledged
            hasDefaultWikiAccess: data ? !!data.hasDefaultWikiAccess : false,
            permissionError: data && data.permissionError,
            isTfvcOnlyProject: data ? !!data.isTfvcOnlyProject : false,
            isProjectWikiExisting: data ? !!data.isProjectWikiExisting : false,
            isUserGivenWiki: data ? !!data.isUserGivenWiki : false,
            isRepositoryDisabled: data ? !!data.isRepositoryDisabled : false,
            isLoading,
            isInitialTreeDataPopulated: data ? !!data.isInitialTreeDataPopulated : false,
            isIllegalPath
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._historyService.subscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);

        if (this._reattemptDataProvider) {
            // we don't want to reattempt again
            this._reattemptDataProvider = false;
            // call dataprovider in did mount to make the dataprovider resolution more reliable
            this.trackPromise(
                this._contributionService.getDataAsync<WikiDataProviderData>(WikiDataProviderContributionId, this._reattemptRequestParameters)
            ).promise.then(
                (data: WikiDataProviderData) => {
                    if (data) {
                        const wikiInfo = _getWikiInfo(this.context.pageContext, data);
                        const wikiPageInfo = _getWikiPageInfo(data);
                        if (wikiInfo) {
                            _updateInitialUrl(this.context.pageContext, wikiInfo, wikiPageInfo, this.state.wikiViewOptions);
                        }

                        const { pagePath } = this._historyService.getState();

                        _publishNonConformantPage(this.context.pageContext, wikiPageInfo, wikiInfo);

                        this.setState({
                            wikiInfo,
                            wikiPageInfo,
                            showPublishCodeWikiAction: _getPublishCodeWikiActionFlag(data, wikiInfo),
                            showCreateNewProjectWikiAction: _getCreateNewProjectWikiActionFlag(data, wikiInfo),
                            wikiPages: data.pages,
                            isHandleBrokenLinksEnabled: _getHandleBrokenLinksFlag(data),
                            isEmptyWiki: _checkIfEmptyWiki(this.context.pageContext, data, pagePath),
                            isStakeHolder: data.isStakeholder,
                            hasDefaultWikiAccess: !!data.hasDefaultWikiAccess,
                            permissionError: data.permissionError,
                            isTfvcOnlyProject: !!data.isTfvcOnlyProject,
                            isProjectWikiExisting: !!data.isProjectWikiExisting,
                            isUserGivenWiki: data.isUserGivenWiki,
                            isLoading: false,
                            isInitialTreeDataPopulated: !!data.isInitialTreeDataPopulated
                        });
                    } else {
                        const providerExceptions = this._contributionService.getDataProviderExceptions();

                        if (providerExceptions) {
                            const ex = providerExceptions[WikiDataProviderContributionId];
                            if (ex && ex.exceptionType === "WikiIllegalCharacterPathException") {
                                this.setState({ isIllegalPath: true });
                            }
                        }

                        this.setState({ isLoading: false });
                    }
                },
                () => {
                    this.setState({ isLoading: false });
                }
            );
        } else {
            const { wikiInfo, wikiPageInfo, wikiViewOptions } = this.state;
            if (wikiInfo) {
                _updateInitialUrl(this.context.pageContext, wikiInfo, wikiPageInfo, wikiViewOptions);
            }
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this._historyService.unsubscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);
    }

    public render(): JSX.Element {
        const { wikiViewOptions, wikiInfo, wikiPageInfo, showCreateNewProjectWikiAction, showPublishCodeWikiAction, isLoading } = this.state;

        return isLoading ? (
            <Spinner className="flex-grow" label={Resources.LoadingSpinnerLabel} />
        ) : (
            <>
                {wikiInfo ? (
                    <Page className="flex-grow">
                        <OverviewContainer
                            wikiInfo={wikiInfo}
                            wikiPageInfo={wikiPageInfo}
                            wikiViewOptions={wikiViewOptions}
                            showPublishCodeWikiAction={showPublishCodeWikiAction}
                            showCreateNewProjectWikiAction={showCreateNewProjectWikiAction}
                            wikiPages={this.state.wikiPages}
                            isHandleBrokenLinksEnabled={this.state.isHandleBrokenLinksEnabled}
                            isEmptyWiki={this.state.isEmptyWiki}
                            isInitialTreeDataPopulated={this.state.isInitialTreeDataPopulated}
                            isPageCommentsReadOnly={!wikiInfo.wikiPermissions.hasWikiPageCommentsWritePermission}
                        />
                    </Page>
                ) : (
                    this._renderNoWikiExperiences()
                )}
            </>
        );
    }

    // Public for UT
    public _onHistoryPopped = (popStateEvent: INavigationStateChangedEvent): void => {
        const { newState, oldState } = popStateEvent;
        const oldWikiIdentitifer = _getWikiIdentitiferFromNavHistoryEntry(oldState);
        const newWikiIdentitifer = _getWikiIdentitiferFromNavHistoryEntry(newState);

        // Skip handling of back navigation through HistoryPopped in case:
        // a. route id is different
        // b. wiki identitifer is different
        if (newState && newState.state && oldState && oldState.routeId === newState.routeId && oldWikiIdentitifer === newWikiIdentitifer) {
            setWikiWindowTitle(this.context.pageContext, newState.state);
            const urlParameters = _getNormalizedUrlParameters(newState.state);

            const wikiPageInfo: WikiPageInfo = {
                path: urlParameters.pagePath || (newState.data && newState.data.pagePath),
                id: urlParameters.pageId
            } as WikiPageInfo;
            const wikiViewOptions: WikiViewOptions = {
                action: urlParameters._a || ActionIds.View,
                anchor: urlParameters.anchor
            };
            this.setState({ wikiPageInfo, wikiViewOptions });
        }
    };

    private _renderNoWikiExperiences(): JSX.Element {
        return (
            <WrappedComponent
                wrappedType={"Wiki.NoWiki"}
                dependencies={["ms.vss-wiki-web.wiki-nowiki-experience"]}
                isStakeHolder={this.state.isStakeHolder}
                hasDefaultWikiAccess={this.state.hasDefaultWikiAccess}
                permissionError={this.state.permissionError}
                isTfvcOnlyProject={this.state.isTfvcOnlyProject}
                isProjectWikiExisting={this.state.isProjectWikiExisting}
                isUserGivenWiki={this.state.isUserGivenWiki}
                isIllegalPath={this.state.isIllegalPath}
                isRepositoryDisabled={this.state.isRepositoryDisabled}
            />
        );
    }
}

// exported for UT
export function _publishNonConformantPage(
    pageContext: IVssPageContext,
    wikiPageInfo: WikiPageInfo | undefined,
    wikiInfo: WikiInfo | undefined
): void {
    if (wikiPageInfo && wikiPageInfo.isNonConformant) {
        publish(pageContext, TelemetryConstants.NonConformantPageSelected, wikiInfo && wikiInfo.wiki ? wikiInfo.wiki.type : undefined, {
            path: wikiPageInfo.path
        });
    }
}

// exported for UT
export function _getWikiIdentitiferFromNavHistoryEntry(navState: INavigationHistoryEntry | undefined): string | undefined {
    let wikiIdentitifer: string | undefined = undefined;
    if (navState && navState.state) {
        wikiIdentitifer = (navState.state as UrlParameters).wikiIdentifier;
    }

    return wikiIdentitifer;
}

// exported for UT
export function _updateInitialUrl(
    pageContext: IVssPageContext,
    wikiInfo: WikiInfo,
    wikiPageInfo: WikiPageInfo | undefined,
    wikiViewOptions: WikiViewOptions
): void {
    let urlParameters: UrlParameters;
    // If you don't get the landing page path, retain the existing pagepath
    // this is needed incase of broken link (page not found)
    urlParameters = {
        _a: wikiViewOptions.action
    };

    if (wikiPageInfo) {
        urlParameters = {
            ...urlParameters,
            pagePath: wikiPageInfo.path,
            pageId: wikiPageInfo.id
        };
    }

    const wikiInfoParameters = {
        [UrlConstants.WikiIdentifierParam]: wikiInfo.wiki.name,
        [UrlConstants.WikiVersionParam]: getVersionFromBranchName(wikiInfo.wikiVersion.version)
    };

    updateUrl(pageContext, urlParameters, wikiInfoParameters, StateMergeOptions.currentState, true);
}

// exported for UT
export function _getWikiInfo(pageContext: IVssPageContext, data: WikiDataProviderData | undefined): WikiInfo | undefined {
    return data && data.landingWiki && data.landingWikiVersion
        ? {
              wiki: data.landingWiki,
              wikiVersion: data.landingWikiVersion,
              wikiRepositoryData: data.repositoryData,
              wikiPermissions: _getWikiPermissions(pageContext, data.landingWiki, data.isStakeholder)
          }
        : undefined;
}

// exported for UT
export function _getWikiPageInfo(data: WikiDataProviderData | undefined): WikiPageInfo | undefined {
    return !!data && !!data.landingWikiPage
        ? {
              ...data.landingWikiPage,
              version: data.landingPageVersion
          }
        : undefined;
}

// exported for UT
export function _getWikiPermissions(pageContext: IVssPageContext, wiki: WikiV2, isStakeholder: boolean): WikiPermission {
    return {
        hasWikiRenamePermission: WikiPermissionsHelper.hasWikiRenamePermission(pageContext, wiki, isStakeholder),
        hasWikiContributePermission: WikiPermissionsHelper.hasWikiContributePermission(pageContext, wiki, isStakeholder),
        hasWikiManagePermission: WikiPermissionsHelper.hasWikiManagePermission(pageContext, wiki, isStakeholder),
        hasWikiCreatePermission: WikiPermissionsHelper.hasWikiCreatePermission(pageContext, wiki.projectId, isStakeholder),
        hasWikiPageCommentsWritePermission: WikiPermissionsHelper.hasWikiPageCommentsWritePermission(pageContext, wiki, isStakeholder)
    };
}

// exported for UT
export function _getNormalizedUrlParameters(urlParameters: UrlParameters, defaultUrlParameters?: UrlParameters): UrlParameters {
    const urlParam: UrlParameters = { ...defaultUrlParameters, ...urlParameters };

    if (urlParam._a) {
        urlParam._a = urlParam._a.toLowerCase();
    }

    if (urlParam.pagePath) {
        urlParam.pagePath = normalizeWikiPagePath(urlParam.pagePath);
    }

    return urlParam;
}

// exported for UT
export function _getPublishCodeWikiActionFlag(data: WikiDataProviderData | undefined, wikiInfo: WikiInfo | undefined): boolean {
    return Boolean(data && wikiInfo && !data.isTfvcOnlyProject && wikiInfo.wikiPermissions.hasWikiContributePermission);
}

// exported for UT
export function _getCreateNewProjectWikiActionFlag(data: WikiDataProviderData | undefined, wikiInfo: WikiInfo | undefined): boolean {
    return Boolean(data && wikiInfo && !data.isProjectWikiExisting && wikiInfo.wikiPermissions.hasWikiCreatePermission);
}

// exported for UT
export function _getHandleBrokenLinksFlag(data: WikiDataProviderData | undefined): boolean {
    return Boolean(data && data.isBrokenLinksHandlingEnabled);
}

// exported for UT
export function _checkIfEmptyWiki(pageContext: IVssPageContext, data: WikiDataProviderData | undefined, pagePath: string | undefined): boolean {
    return Boolean(
        !pagePath &&
            data &&
            data.landingWiki &&
            (data.landingWiki.type === WikiType.ProjectWiki || isCodeEditEnabled(pageContext)) &&
            !data.landingWikiPage &&
            (!data.pages || data.pages.length === 1)
    );
}

VssComponent.register("TFS.Wiki.View", View);
